import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  ShieldCheck,
  X,
} from 'lucide-react';
import api from '../../utils/api';
import WechatQrFlow from '../auth/WechatQrFlow';
import {
  confirmEmailBinding,
  createWechatBindingSession,
  pollWechatBindingSession,
  sendEmailBindingCode,
} from '../auth/wechatAuthApi';

const META = {
  wechat: { icon: MessageCircle, title: '绑定微信', titleEn: 'Bind WeChat' },
  phone: { icon: Phone, title: '绑定手机号', titleEn: 'Bind phone' },
  email: { icon: Mail, title: '绑定邮箱', titleEn: 'Bind email' },
};

function errorMessage(error, fallback, english) {
  const code = error?.code || error?.response?.data?.code || error?.response?.data?.data?.error_code;
  const messages = {
    LOGIN_METHOD_ALREADY_BOUND: '当前账号已经绑定该登录方式',
    LOGIN_IDENTITY_IN_USE: '该身份已被其他账号使用',
    VERIFICATION_CODE_INVALID: '验证码不正确',
    VERIFICATION_CODE_EXPIRED: '验证码已过期，请重新获取',
    VERIFICATION_RATE_LIMITED: '操作过于频繁，请稍后再试',
    MAIL_NOT_CONFIGURED: '暂时无法发送邮件，请稍后重试或联系客服',
    MAIL_SEND_FAILED: '验证码邮件发送失败，请稍后再试',
  };
  return getErrorMessage(messages[code]
    || error?.response?.data?.error
    || error?.response?.data?.message
    || error?.message
    || fallback);
}

export default function LoginMethodBindingDialog({ method, onClose, onBound, english = false, darkActive = false }) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [emailStep, setEmailStep] = useState('request');
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const meta = META[method] || META.wechat;
  const Icon = meta.icon;
  const cleanPhone = phone.replace(/\D/g, '').slice(0, 11);
  const phoneValid = /^1[3-9]\d{9}$/.test(cleanPhone);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = window.setTimeout(() => setCountdown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const completeBinding = useCallback(async () => {
    await onBound(method);
    onClose();
  }, [method, onBound, onClose]);

  const handleWechatAuthorized = useCallback(async () => {
    setFeedback({ type: 'success', message: english ? 'WeChat bound.' : '微信绑定成功' });
    await completeBinding();
  }, [completeBinding, english]);

  const sendPhoneCode = async () => {
    if (!phoneValid || sending || countdown > 0) return;
    setSending(true);
    setFeedback({ type: '', message: '' });
    try {
      const response = await api.post('/users/me/login-methods/phone/code', { phone: `+86${cleanPhone}` });
      setCountdown(60);
      if (response.data?.dev_code) {
        setCode(response.data.dev_code);
        setFeedback({ type: 'info', message: english ? 'Development code filled automatically.' : `开发模式：验证码 ${response.data.dev_code} 已自动填入` });
      } else {
        setFeedback({ type: 'success', message: english ? 'Code sent.' : '验证码已发送，请留意短信' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(errorMessage(error, '短信发送失败，请稍后再试', english)) });
    } finally {
      setSending(false);
    }
  };

  const bindPhone = async event => {
    event.preventDefault();
    if (!phoneValid || !/^\d{4,6}$/.test(code) || submitting) return;
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    try {
      await api.post('/users/me/login-methods/phone/bind', { phone: `+86${cleanPhone}`, code });
      await completeBinding();
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(errorMessage(error, '手机号绑定失败，请稍后再试', english)) });
    } finally {
      setSubmitting(false);
    }
  };

  const sendEmailCode = async event => {
    event.preventDefault();
    if (!emailValid || sending || countdown > 0) return;
    setSending(true);
    setFeedback({ type: '', message: '' });
    try {
      const result = await sendEmailBindingCode(email.trim());
      setCountdown(Number(result?.resend_after_seconds) || 60);
      setEmailStep('confirm');
      setFeedback({ type: 'success', message: english ? 'Code sent. Check your inbox.' : `验证码已发送至 ${result?.email || '该邮箱'}` });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(errorMessage(error, '验证码邮件发送失败，请稍后再试', english)) });
    } finally {
      setSending(false);
    }
  };

  const bindEmail = async event => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code) || password.length < 8 || password !== passwordConfirmation || submitting) return;
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    try {
      await confirmEmailBinding({
        email: email.trim(),
        code,
        password,
        password_confirmation: passwordConfirmation,
      });
      await completeBinding();
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(errorMessage(error, '邮箱绑定失败，请稍后再试', english)) });
    } finally {
      setSubmitting(false);
    }
  };

  const body = method === 'wechat' ? (
    <div className="account-binding-dialog__wechat">
      <WechatQrFlow
        createSession={createWechatBindingSession}
        pollSession={pollWechatBindingSession}
        onAuthorized={handleWechatAuthorized}
        purpose="bind"
        darkActive={darkActive}
        english={english}
      />
      <p>{english ? 'Scanning only links your WeChat identity. It does not read unrelated data.' : '扫码仅用于绑定微信身份，不会读取无关微信资料。'}</p>
    </div>
  ) : method === 'phone' ? (
    <form className="account-binding-form" onSubmit={bindPhone}>
      <label>
        <span>{english ? 'Phone number' : '手机号'}</span>
        <div className="account-binding-field">
          <b>+86</b>
          <input type="tel" inputMode="numeric" autoComplete="tel" value={cleanPhone} onChange={event => setPhone(event.target.value)} placeholder={english ? 'Phone number' : '请输入手机号'} />
        </div>
      </label>
      <label>
        <span>{english ? 'Verification code' : '短信验证码'}</span>
        <div className="account-binding-field">
          <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={english ? '4–6 digit code' : '4–6 位验证码'} />
          <button type="button" onClick={sendPhoneCode} disabled={!phoneValid || sending || countdown > 0}>
            {sending ? (english ? 'Sending…' : '发送中…') : countdown > 0 ? `${countdown}s` : (english ? 'Send code' : '获取验证码')}
          </button>
        </div>
      </label>
      <button className="account-binding-submit" type="submit" disabled={!phoneValid || !/^\d{4,6}$/.test(code) || submitting}>
        {submitting && <Loader2 className="account-spin" size={14} />}
        {submitting ? (english ? 'Binding…' : '绑定中…') : (english ? 'Bind phone' : '绑定手机号')}
      </button>
    </form>
  ) : emailStep === 'request' ? (
    <form className="account-binding-form" onSubmit={sendEmailCode}>
      <label>
        <span>{english ? 'Email' : '邮箱'}</span>
        <div className="account-binding-field">
          <input type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@example.com" />
        </div>
      </label>
      <p className="account-binding-form__hint">{english ? 'We will send a six-digit code to verify ownership.' : '我们会发送六位验证码，用于确认邮箱归属。'}</p>
      <button className="account-binding-submit" type="submit" disabled={!emailValid || sending}>
        {sending && <Loader2 className="account-spin" size={14} />}
        {sending ? (english ? 'Sending…' : '发送中…') : (english ? 'Send verification code' : '发送邮件验证码')}
      </button>
    </form>
  ) : (
    <form className="account-binding-form" onSubmit={bindEmail}>
      <button className="account-binding-back" type="button" onClick={() => { setEmailStep('request'); setCode(''); setFeedback({ type: '', message: '' }); }}>
        <ArrowLeft size={13} />{english ? 'Change email' : '更换邮箱'}
      </button>
      <div className="account-binding-email-target"><Mail size={15} /><span>{email}</span></div>
      <label>
        <span>{english ? 'Email code' : '邮件验证码'}</span>
        <div className="account-binding-field">
          <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={english ? 'Six-digit code' : '六位验证码'} />
          <button type="button" onClick={sendEmailCode} disabled={sending || countdown > 0}>
            {sending ? (english ? 'Sending…' : '发送中…') : countdown > 0 ? `${countdown}s` : (english ? 'Resend' : '重新发送')}
          </button>
        </div>
      </label>
      <label>
        <span>{english ? 'Login password' : '设置登录密码'}</span>
        <div className="account-binding-field"><input type="password" autoComplete="new-password" value={password} onChange={event => setPassword(event.target.value)} maxLength={64} placeholder={english ? 'At least 8 characters' : '至少 8 个字符'} /></div>
      </label>
      <label>
        <span>{english ? 'Confirm password' : '确认登录密码'}</span>
        <div className="account-binding-field"><input type="password" autoComplete="new-password" value={passwordConfirmation} onChange={event => setPasswordConfirmation(event.target.value)} maxLength={64} placeholder={english ? 'Enter password again' : '再次输入密码'} /></div>
      </label>
      {passwordConfirmation && password !== passwordConfirmation && <p className="account-binding-form__error">{english ? 'Passwords do not match.' : '两次输入的密码不一致'}</p>}
      <button className="account-binding-submit" type="submit" disabled={!/^\d{6}$/.test(code) || password.length < 8 || password !== passwordConfirmation || submitting}>
        {submitting && <Loader2 className="account-spin" size={14} />}
        {submitting ? (english ? 'Binding…' : '绑定中…') : (english ? 'Verify and bind' : '验证并绑定邮箱')}
      </button>
    </form>
  );

  return createPortal(
    <div className="account-binding-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className="account-binding-dialog" role="dialog" aria-modal="true" aria-labelledby="account-binding-title">
        <header>
          <span className="account-binding-dialog__icon"><Icon size={18} /></span>
          <div><span className="account-eyebrow">LOGIN METHOD</span><h2 id="account-binding-title">{english ? meta.titleEn : meta.title}</h2></div>
          <button type="button" onClick={onClose} aria-label={english ? 'Close' : '关闭'}><X size={16} /></button>
        </header>
        <div className="account-binding-dialog__body">
          {feedback.message && <div className={`account-binding-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>{feedback.type === 'error' ? <X size={14} /> : <ShieldCheck size={14} />}<span>{feedback.message}</span></div>}
          {body}
        </div>
      </section>
    </div>,
    document.body,
  );
}
